/**
 * Re-reads a listing after a save.
 *
 * Writes are eventually consistent: the API returns as soon as the command is
 * accepted, and a subscriber then projects it into Elasticsearch — which is
 * what the listings actually read. Measured against the deployed stack, that
 * projection lands about 600ms after the request returns.
 *
 * So neither of the obvious options works on its own. Refetching immediately
 * reads the pre-save document and the row appears unchanged; not refetching at
 * all leaves the listing showing the form payload, which has none of the
 * fields the server derives (a generated code, the audit stamps), which is why
 * a saved row looked stale until the page was reloaded by hand.
 *
 * This fires a couple of times across the window instead of betting on one
 * delay. The first pass usually already has the change; the second covers a
 * slower projection without making the user wait on the common case.
 */

/** Comfortably past the ~600ms measured projection lag. */
const FIRST_PASS_MS = 900;
/** Backstop for a slower run — a stale row is worse than one extra read. */
const SECOND_PASS_MS = 2500;

/**
 * Schedules the refetches and returns a cancel function, so a component that
 * unmounts (the user navigates away straight after saving) doesn't fire a
 * request against a listing nobody is looking at.
 */
export function refreshAfterSave(refetch: () => void): () => void {
  const timers = [
    window.setTimeout(refetch, FIRST_PASS_MS),
    window.setTimeout(refetch, SECOND_PASS_MS),
  ];

  return () => timers.forEach((id) => window.clearTimeout(id));
}
